import Honeybadger from 'honeybadger-js';
import React, { useMemo } from 'react';

import { useSendLocalHotelSmsMutation } from '../../../generated/graphql';
import { getChangedValues } from '../../../helpers/getChangedValues';
import { splitHouseholds } from '../../../helpers/households';
import { editAddressCollectionSmsRoute, editShareLocalHotelSmsPath } from '../../../helpers/routes';
import SmsSequence, { ShareValues } from '../../SharePage/SmsSequence';
import useShareTracking from '../../SharePage/useShareTracking';
import { useFilteredHouseholds } from '../GuestFilters/useFilteredHouseholds';
import { useSequenceState } from './useSequenceState';

const ShareLocalHotelSMS: React.FC = () => {
  //TODO: Update the mutation to send the local hotels SMS
  const [shareLocalHotelSms, { loading: sendingMessage }] = useSendLocalHotelSmsMutation();
  const {
    sequence,
    wwsLoading,
    guestIds,
    setGuestIds,
    setHideRecaptcha,
    hideRecaptcha,
    dispatch,
    recaptchaRef,
    seqId,
    showError,
    errorMessage,
    showSuccess,
    householdLoading,
    wwsData,
    greeting,
    defaultMessage,
    optout,
  } = useSequenceState();
  const { filteredHouseholds, setFilterCriteria } = useFilteredHouseholds();
  const households = useMemo(() => splitHouseholds('phone', filteredHouseholds), [filteredHouseholds]);

  const track = useShareTracking();

  const message: string = wwsData?.weddingWebsite.pinProtect
    ? `${defaultMessage} Our site password is: ${wwsData.weddingWebsite.pin}.`
    : defaultMessage;

  let initialValues: ShareValues = {
    greeting,
    message,
    optout,
  };

  const smsLink = `https://${wwsData?.weddingWebsite.wwsUrl}`;

  const sendMessage = async (values: ShareValues) => {
    if (wwsLoading || sendingMessage) {
      return null;
    }

    const edited = getChangedValues(initialValues, values);

    track('Website Shared', {
      guestMessagedCount: guestIds.length,
      message: values.message,
      messageCustomized: Boolean(Object.keys(edited).length),
      messageType: sequence?.messagingSequence?.displayName,
      medium: 'sms',
    });

    dispatch({ type: 'RESET', payload: { hideRecaptcha: true, showSuccess: false, showError: false } });

    try {
      await shareLocalHotelSms({
        variables: {
          args: {
            body: values.message,
            guestIds,
            token: recaptchaRef.current?.getValue() || '',
          },
        },
      });
      dispatch({ type: 'SUCCESS' });
    } catch (error) {
      Honeybadger.notify(error, 'Error sending share local hotels sms', { context: { error } });
      dispatch({ type: 'ERROR', payload: { message: error.message } });
    } finally {
      recaptchaRef.current?.reset();
    }
  };

  return (
    <SmsSequence
      onSend={sendMessage}
      loading={sendingMessage}
      setGuestIds={setGuestIds}
      showError={showError}
      errorMessage={errorMessage}
      showSuccess={showSuccess}
      initialValues={initialValues}
      hideRecaptcha={hideRecaptcha}
      setHideRecaptcha={setHideRecaptcha}
      recaptchaRef={recaptchaRef}
      editPath={editShareLocalHotelSmsPath}
      editRoute={editAddressCollectionSmsRoute(seqId)}
      households={households}
      wwsLoading={wwsLoading}
      householdLoading={householdLoading}
      smsLink={smsLink}
      displayName={sequence?.messagingSequence?.displayName}
      setFilter={setFilterCriteria}
    />
  );
};

export default ShareLocalHotelSMS;
